import * as THREE from 'three';
import { GeometryBuilder } from './geometry-builder';
export const ROAD_FAMILIES = [
  'compact',
  'hatchback',
  'car',
  'suv',
  'wagon',
  'pickup',
  'sport',
  'van',
  'minivan',
  'delivery',
  'bus',
  'coach',
  'truck',
  'flatbed',
  'motorcycle',
  'scooter',
] as const;
export type RoadFamily = (typeof ROAD_FAMILIES)[number];
export interface RoadVehicleSpec {
  name: string;
  width: number;
  length: number;
  height: number;
  wheel: number;
  axles: number;
}
export const ROAD_SPECS: Record<RoadFamily, RoadVehicleSpec> = {
  compact: { name: 'Short city compact', width: 1.62, length: 3.1, height: 1.45, wheel: 0.29, axles: 2 },
  hatchback: { name: 'Five-door hatchback', width: 1.73, length: 3.65, height: 1.52, wheel: 0.31, axles: 2 },
  car: { name: 'Notchback sedan', width: 1.78, length: 4.2, height: 1.5, wheel: 0.32, axles: 2 },
  suv: { name: 'Utility SUV', width: 1.96, length: 4.6, height: 1.93, wheel: 0.4, axles: 2 },
  wagon: { name: 'Long-roof estate', width: 1.82, length: 4.55, height: 1.57, wheel: 0.33, axles: 2 },
  pickup: { name: 'Open-bed pickup', width: 1.98, length: 5.0, height: 1.86, wheel: 0.4, axles: 2 },
  sport: { name: 'Low fastback coupe', width: 1.93, length: 4.1, height: 1.16, wheel: 0.32, axles: 2 },
  van: { name: 'Panel van', width: 1.9, length: 4.7, height: 2.25, wheel: 0.35, axles: 2 },
  minivan: { name: 'Passenger minivan', width: 1.88, length: 4.75, height: 1.85, wheel: 0.34, axles: 2 },
  delivery: { name: 'Step-in parcel van', width: 2.18, length: 5.8, height: 2.8, wheel: 0.4, axles: 2 },
  bus: { name: 'Low-floor city bus', width: 2.4, length: 8.7, height: 2.95, wheel: 0.45, axles: 2 },
  coach: {
    name: 'Three-axle intercity coach',
    width: 2.48,
    length: 10.6,
    height: 3.3,
    wheel: 0.47,
    axles: 3,
  },
  truck: { name: 'Box-body freight truck', width: 2.3, length: 7.0, height: 3.2, wheel: 0.46, axles: 2 },
  flatbed: { name: 'Three-axle flatbed', width: 2.32, length: 7.7, height: 2.8, wheel: 0.46, axles: 3 },
  motorcycle: {
    name: 'Sport motorcycle and rider',
    width: 0.74,
    length: 2.05,
    height: 1.7,
    wheel: 0.32,
    axles: 2,
  },
  scooter: {
    name: 'Step-through scooter and rider',
    width: 0.67,
    length: 1.8,
    height: 1.73,
    wheel: 0.24,
    axles: 2,
  },
};
export const isRoadFamily = (kind: string): kind is RoadFamily => kind in ROAD_SPECS;
type Point = { x: number; y: number; z: number };
function face(g: GeometryBuilder, a: Point, b: Point, c: Point, d: Point, color: number) {
  const normal = new THREE.Vector3()
    .subVectors(new THREE.Vector3(b.x, b.y, b.z), new THREE.Vector3(a.x, a.y, a.z))
    .cross(new THREE.Vector3(d.x - a.x, d.y - a.y, d.z - a.z))
    .normalize();
  g.quad(a, b, c, d, normal, color);
}
/** Sloping windscreens and tapered shoulders are actual silhouette geometry. */
function loft(
  g: GeometryBuilder,
  sections: { z: number; width: number; top: number; bottom: number; roof?: number }[],
  color: number,
) {
  const rings = sections.map((s) => [
    { x: -s.width / 2, y: s.bottom, z: s.z },
    { x: s.width / 2, y: s.bottom, z: s.z },
    { x: (s.width * (s.roof ?? 1)) / 2, y: s.top, z: s.z },
    { x: (-s.width * (s.roof ?? 1)) / 2, y: s.top, z: s.z },
  ]);
  face(g, rings[0][0], rings[0][3], rings[0][2], rings[0][1], color);
  const end = rings.at(-1)!;
  face(g, end[0], end[1], end[2], end[3], color);
  for (let i = 1; i < rings.length; i++)
    for (let k = 0; k < 4; k++) {
      const n = (k + 1) % 4;
      face(g, rings[i - 1][k], rings[i - 1][n], rings[i][n], rings[i][k], color);
    }
}
export function roadVehicleGeometry(kind: RoadFamily): THREE.BufferGeometry {
  const g = new GeometryBuilder(),
    s = ROAD_SPECS[kind],
    w = s.width,
    l = s.length,
    h = s.height,
    front = -l / 2,
    rear = l / 2;
  const glass = 0x355b68,
    trim = 0x536b70;
  if (kind === 'motorcycle' || kind === 'scooter') {
    const scooter = kind === 'scooter';
    g.box(-0.1, 0.32, front + 0.25, 0.2, 0.12, l - 0.45, trim);
    loft(
      g,
      [
        { z: front + 0.12, width: 0.18, top: scooter ? 1.02 : 0.85, bottom: 0.38 },
        { z: front + 0.65, width: scooter ? 0.48 : 0.62, top: scooter ? 0.62 : 0.95, bottom: 0.32 },
        { z: rear - 0.1, width: 0.4, top: 0.83, bottom: 0.45 },
      ],
      0xffffff,
    );
    g.box(-0.23, 0.84, -0.03, 0.46, 0.12, 0.6, 0x32474c);
    g.box(-0.34, 1.05, front + 0.3, 0.68, 0.08, 0.07, trim);
    for (const x of [-0.26, 0.13]) g.box(x, 0.37, -0.14, 0.13, 0.58, 0.24, 0x45545b);
    loft(
      g,
      [
        { z: -0.13, width: 0.4, top: 1.46, bottom: 0.97 },
        { z: 0.2, width: 0.44, top: 1.5, bottom: 0.92 },
      ],
      0x788d89,
    );
    for (const x of [-0.3, 0.2]) g.box(x, 1.03, -0.48, 0.1, 0.32, 0.42, 0x839792);
    g.box(-0.18, 1.48, -0.12, 0.36, 0.28, 0.33, 0xe7d5b5);
    g.box(-0.18, 1.58, -0.13, 0.36, 0.12, 0.05, glass);
    g.box(-0.09, 0.95, front + 0.04, 0.18, 0.16, 0.06, 0xffe0a1);
    return g.geometry();
  }
  const commercial = ['van', 'delivery', 'bus', 'coach', 'truck', 'flatbed'].includes(kind);
  const bodyTop = kind === 'sport' ? 0.69 : kind === 'suv' || kind === 'pickup' ? 1.05 : 0.91;
  loft(
    g,
    [
      { z: front, width: w * 0.88, top: bodyTop - 0.13, bottom: 0.38 },
      { z: front + 0.2, width: w, top: bodyTop, bottom: 0.34 },
      { z: rear - 0.18, width: w, top: bodyTop, bottom: 0.34 },
      { z: rear, width: w * 0.91, top: bodyTop - 0.08, bottom: 0.4 },
    ],
    0xffffff,
  );
  if (kind === 'truck' || kind === 'flatbed') {
    loft(
      g,
      [
        { z: front + 0.05, width: w * 0.92, top: h - 0.35, bottom: 0.85 },
        { z: front + 1.85, width: w * 0.95, top: h - 0.35, bottom: 0.85 },
      ],
      0xffffff,
    );
    g.box(-w * 0.38, 1.52, front - 0.005, w * 0.76, 0.9, 0.065, glass);
    for (const x of [-w * 0.5 - 0.03, w * 0.5 - 0.04]) g.box(x, 1.5, front + 0.25, 0.07, 0.84, 1.05, glass);
    if (kind === 'truck') {
      g.box(-w * 0.49, 1.0, front + 2.05, w * 0.98, h - 1, l - 2.13, 0xd3ded7);
      for (let z = front + 2.2; z < rear - 0.2; z += 0.46)
        for (const x of [-w * 0.5, w * 0.5 - 0.035]) g.box(x, 1.05, z, 0.035, h - 1.1, 0.06, 0xb4c7bf);
      g.box(-w * 0.44, 1.1, rear - 0.02, w * 0.88, h - 1.22, 0.05, 0x9fb3ae);
    } else {
      g.box(-w * 0.52, 0.94, front + 2.03, w * 1.04, 0.16, l - 2.1, 0xaa9475);
      for (const x of [-w * 0.5, w * 0.5 - 0.075])
        g.box(x, 1.1, front + 2.05, 0.075, 0.36, l - 2.13, 0xd2d7c6);
      for (const z of [-0.1, 0.5, 1.1]) g.box(-0.7, 1.1, z, 1.4, 0.6, 0.44, 0xa38a65);
    }
  } else if (kind === 'bus' || kind === 'coach') {
    const coach = kind === 'coach',
      floor = coach ? 1.05 : 0.77;
    g.box(-w * 0.48, floor, front + 0.12, w * 0.96, 0.15, l - 0.24, 0x657f7b);
    for (const x of [-w / 2, w / 2 - 0.09])
      for (let z = front + 0.28; z < rear - 0.2; z += 0.78) {
        if (x > 0 && z < front + 1.84) continue;
        g.box(x, 0.87, z, 0.09, h - 1.0, 0.7, 0xffffff);
        g.box(x + (x < 0 ? -0.005 : 0.03), h - 1.3, z + 0.06, 0.065, 0.91, 0.58, glass);
      }
    g.box(-w * 0.49, h - 0.22, front + 0.15, w * 0.98, 0.22, l - 0.3, 0xe6e8d9);
    loft(
      g,
      [
        { z: front + 0.01, width: w * 0.95, top: h - 0.22, bottom: 0.91, roof: 0.92 },
        { z: front + 0.24, width: w, top: h - 0.16, bottom: 0.91, roof: 0.95 },
      ],
      glass,
    );
    g.box(-w / 2, 0.87, rear - 0.12, w, h - 0.99, 0.12, 0xffffff);
    for (const x of [-w * 0.42, 0.13])
      for (let z = front + 2; z < rear - 0.5; z += 0.92)
        g.box(x, floor + 0.15, z, w * 0.35, 0.57, 0.7, 0x638c8d);
    if (coach) g.box(-0.5, h, front + 2, 1, 0.22, l - 4, 0xabc0b9);
    else g.box(-0.64, h - 0.01, 0, 1.28, 0.16, 1.6, 0xb8cbc1);
  } else if (kind === 'van' || kind === 'delivery') {
    const delivery = kind === 'delivery',
      cab = front + (delivery ? 1.6 : 1.4);
    if (delivery) {
      loft(
        g,
        [
          { z: front + 0.1, width: w * 0.9, top: h - 0.4, bottom: 0.88, roof: 0.93 },
          { z: cab, width: w * 0.96, top: h - 0.08, bottom: 0.88 },
        ],
        0xffffff,
      );
      g.box(-w / 2, h - 0.13, cab, w, 0.12, rear - cab - 0.08, 0xffffff);
      g.box(-w / 2, 0.88, cab, w, 0.12, rear - cab - 0.08, 0x78877c);
      g.box(w / 2 - 0.09, 0.99, cab, 0.09, h - 1.11, rear - cab - 0.08, 0xffffff);
      g.box(-w / 2, 0.99, cab, 0.09, h - 1.11, -0.7 - cab, 0xffffff);
      g.box(-w / 2, 0.99, 0.7, 0.09, h - 1.11, rear - 0.78, 0xffffff);
      g.box(-w / 2, 0.99, rear - 0.09, w, h - 1.11, 0.09, 0xffffff);
      g.box(0.36, 1.03, -0.5, 0.54, 0.1, 2.55, 0xa79979);
      g.box(0.36, 1.64, -0.5, 0.54, 0.1, 2.55, 0xa79979);
    } else
      loft(
        g,
        [
          { z: front + 0.1, width: w * 0.9, top: h - 0.4, bottom: 0.88, roof: 0.93 },
          { z: cab, width: w * 0.96, top: h - 0.08, bottom: 0.88 },
          { z: rear - 0.07, width: w * 0.98, top: h - 0.08, bottom: 0.88 },
        ],
        0xffffff,
      );
    loft(
      g,
      [
        { z: front + 0.06, width: w * 0.78, top: h - 0.47, bottom: 1.12 },
        { z: front + 0.65, width: w * 0.79, top: h - 0.14, bottom: 1.12 },
      ],
      glass,
    );
    for (const x of [-w / 2 - 0.015, w / 2 - 0.025]) {
      g.box(x, 1.21, front + 0.45, 0.04, h - 1.53, 1.01, glass);
      if (!delivery || x > 0) g.box(x, 1.13, cab + 0.15, 0.04, 0.035, l * 0.42, trim);
    }
    g.box(-w * 0.42, 1.02, rear - 0.01, w * 0.84, h - 1.21, 0.025, 0xc4d1c9);
    g.box(-0.025, 1.02, rear + 0.016, 0.05, h - 1.21, 0.025, trim);
  } else {
    const upright = ['suv', 'wagon', 'minivan', 'compact'].includes(kind);
    const start =
      kind === 'sport'
        ? -0.13 * l
        : kind === 'pickup'
          ? front + 0.7
          : kind === 'minivan'
            ? front + 0.35
            : kind === 'compact'
              ? front + 0.48
              : front + 0.24 * l;
    const end = kind === 'pickup' ? 0.01 * l : upright ? rear - 0.22 : rear - 0.23 * l;
    const roofFront =
        start + (kind === 'sport' || kind === 'minivan' ? 0.65 : kind === 'compact' ? 0.5 : 0.32),
      roofRear = end - (kind === 'compact' ? 0.42 : kind === 'hatchback' ? 0.58 : upright ? 0.15 : 0.3);
    loft(
      g,
      [
        { z: start, width: w * 0.86, top: bodyTop + 0.05, bottom: bodyTop },
        { z: roofFront, width: w * 0.86, top: h - 0.08, bottom: bodyTop, roof: 0.84 },
        { z: roofRear, width: w * 0.85, top: h - 0.08, bottom: bodyTop, roof: 0.84 },
        { z: end, width: w * 0.86, top: bodyTop + 0.05, bottom: bodyTop },
      ],
      glass,
    );
    loft(
      g,
      [
        { z: roofFront - 0.04, width: w * 0.7, top: h - 0.045, bottom: h - 0.11 },
        { z: roofFront + 0.1, width: w * 0.72, top: h, bottom: h - 0.11 },
        { z: roofRear - 0.1, width: w * 0.72, top: h, bottom: h - 0.11 },
        { z: roofRear + 0.04, width: w * 0.69, top: h - 0.045, bottom: h - 0.11 },
      ],
      0xf4f4e9,
    );
    for (const x of [-w * 0.432, w * 0.408]) {
      g.box(
        x,
        bodyTop + 0.02,
        roofFront + (roofRear - roofFront) * 0.48,
        0.046,
        h - bodyTop - 0.1,
        0.075,
        0xd9e0d5,
      );
      g.box(x, 0.79, -0.1, 0.032, 0.028, 0.23, trim);
    }
    if (kind === 'pickup') {
      g.box(-w * 0.42, 0.91, 0.2, w * 0.84, 0.1, rear - 0.3, 0x667a72);
      for (const x of [-w / 2, w / 2 - 0.12]) g.box(x, 1.0, 0.18, 0.12, 0.27, rear - 0.2, 0xffffff);
      g.box(-w / 2, 1.0, rear - 0.11, w, 0.27, 0.12, 0xffffff);
    }
    if (kind === 'wagon' || kind === 'suv')
      for (const x of [-w * 0.29, w * 0.26])
        g.box(x, h, roofFront + 0.1, 0.045, 0.09, Math.max(0.2, roofRear - roofFront - 0.2), trim);
    if (kind === 'sport') {
      g.box(-w * 0.46, 0.55, front - 0.015, w * 0.92, 0.13, 0.12, trim);
      g.box(-w * 0.44, h * 0.8, rear - 0.2, w * 0.88, 0.08, 0.18, 0x859c99);
    }
    if (kind === 'suv') {
      g.box(-0.33, 0.88, rear - 0.02, 0.66, 0.66, 0.23, trim);
      g.box(-w * 0.46, 0.35, front - 0.08, w * 0.92, 0.18, 0.16, 0x859895);
    }
    if (kind === 'minivan')
      for (const x of [-w * 0.504, w * 0.49]) {
        g.box(x, 0.91, -0.05, 0.03, 0.05, l * 0.31, trim);
        g.box(x, 0.45, 0.02, 0.03, 0.68, 0.035, trim);
        g.box(x, bodyTop + 0.04, roofRear - 0.55, 0.035, h - bodyTop - 0.11, 0.065, trim);
      }
  }
  // Readable bumpers, mirrors, separated lamps, grilles and wheel-arch trim.
  g.box(-w * 0.44, 0.43, front - 0.045, w * 0.88, 0.13, 0.09, trim);
  g.box(-w * 0.43, 0.46, rear - 0.035, w * 0.86, 0.13, 0.08, trim);
  for (const x of [-w * 0.43, w * 0.24]) {
    g.box(x, 0.71, front - 0.055, w * 0.19, 0.12, 0.065, 0xffe6b3);
    g.box(x, 0.73, rear - 0.015, w * 0.19, 0.12, 0.045, 0xbd6e59);
  }
  for (const x of [-w * 0.57, w * 0.48])
    g.box(
      x,
      commercial ? 1.59 : kind === 'sport' ? 0.81 : 1.02,
      front + (commercial ? 0.62 : kind === 'sport' ? l * 0.48 : l * 0.32),
      w * 0.09,
      0.13,
      0.22,
      trim,
    );
  g.box(-w * 0.25, 0.59, front - 0.06, w * 0.5, 0.08, 0.025, 0x31474a);
  return g.geometry();
}
export function roadWheelGeometry(): THREE.BufferGeometry {
  const g = new GeometryBuilder();
  for (const [radius, width, color] of [
    [0.34, 0.2, 0x334441],
    [0.19, 0.218, 0xb1c2b9],
  ] as const) {
    const geometry = new THREE.CylinderGeometry(radius, radius, width, 12, 1)
      .rotateZ(Math.PI / 2)
      .toNonIndexed();
    const p = geometry.getAttribute('position'),
      n = geometry.getAttribute('normal');
    for (let i = 0; i < p.count; i += 3)
      g.triangle(
        { x: p.getX(i), y: p.getY(i), z: p.getZ(i) },
        { x: p.getX(i + 1), y: p.getY(i + 1), z: p.getZ(i + 1) },
        { x: p.getX(i + 2), y: p.getY(i + 2), z: p.getZ(i + 2) },
        { x: n.getX(i), y: n.getY(i), z: n.getZ(i) },
        color,
      );
    geometry.dispose();
  }
  g.box(-0.113, -0.025, -0.17, 0.226, 0.05, 0.34, 0x556d70);
  g.box(-0.113, -0.17, -0.025, 0.226, 0.34, 0.05, 0x556d70);
  return g.geometry();
}
export function busDoorGeometry(kind: 'bus' | 'coach', sign: number): THREE.BufferGeometry {
  const s = ROAD_SPECS[kind],
    g = new GeometryBuilder(),
    z = -s.length / 2 + 0.28 + (sign > 0 ? 0.76 : 0);
  g.box(s.width / 2 - 0.07, 0.87, z, 0.075, s.height - 1.04, 0.75, 0xd2ded3);
  g.box(s.width / 2 + 0.01, s.height - 1.3, z + 0.07, 0.035, 0.9, 0.61, 0x385e66);
  return g.geometry();
}
export function deliveryDoorGeometry(): THREE.BufferGeometry {
  const s = ROAD_SPECS.delivery,
    g = new GeometryBuilder();
  g.box(-s.width / 2 - 0.015, 0.98, -0.71, 0.095, s.height - 1.1, 1.42, 0xe0e5d9);
  g.box(-s.width / 2 - 0.045, 1.45, -0.55, 0.04, 0.12, 0.23, 0x465d5c);
  g.box(-s.width / 2 - 0.04, 2.0, -0.47, 0.03, 0.36, 0.85, 0x77948a);
  return g.geometry();
}
